import React, { useContext, useEffect } from 'react';

import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSortBy } from 'react-instantsearch-hooks-web';
import { useParams } from 'react-router-dom';

import { TenantCtx } from '../../../../containers/CommonLayout/CommonLayout';
import { useSERPNavigation } from '../../../../utils/hooks';
import { HITS_TYPES } from '../../const';
import { FILTERS } from '../../const';
import { CategoryNavigationBlock } from '../CategoryNavigationBlock';
import { ExpertNavigationBlock } from '../ExpertNavigationBlock';
import { FilterWrapper } from '../FilterWrapper';
import { HierarchicalMenu as CustomHierarchicalMenu } from '../HierarchicalMenu';
import { IndexMenu } from '../IndexMenu';
import { RefinementList as CustomRefinementList } from '../RefinementList';
import useStyles from './styles';

const CATEGORY_FILTER = [FILTERS.Category, FILTERS.Subcategory];

interface FiltersPanelProps {
  isMobile: boolean;
  resetTypeFilter?: boolean;
  isSearching?: boolean;
}

const FiltersPanel: React.FC<FiltersPanelProps> = ({
  isMobile,
  resetTypeFilter = false,
  isSearching = false,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const params = useParams();
  const serpType = useSERPNavigation();
  const { tenant } = useContext(TenantCtx);

  const onCategoriesPage = !!params?.category;
  const onExpertPage = !!params?.expertSlug;
  const onInsightsPage = serpType === HITS_TYPES.NEWS;
  const onGlossaryPage = serpType === HITS_TYPES.GLOSSARY;
  const onPathwaysPage = serpType === HITS_TYPES.PATHWAY;

  const sortedIndexName = `${process.env.REACT_APP_ALGOLIA_INDEX_NAME}_${tenant?.name}_title_asc`;
  const indexName = `${process.env.REACT_APP_ALGOLIA_INDEX_NAME}_${tenant?.name}`;

  const { refine } = useSortBy({
    items: [
      { value: sortedIndexName, label: 'Sort' },
      { value: indexName || '', label: 'Default' },
    ],
  });

  useEffect(() => {
    if (isSearching || !onGlossaryPage) {
      refine(indexName);
    } else {
      refine(sortedIndexName);
    }
  }, [isSearching, refine, onGlossaryPage, sortedIndexName, indexName]);

  return (
    <>
      <Box className={classes.filtersPanel}>
        {onGlossaryPage && (
          <FilterWrapper title={t('page.discover.index')} withoutToggle>
            <IndexMenu attribute={FILTERS.Letter} limit={50} />
          </FilterWrapper>
        )}
        {onExpertPage && (
          <FilterWrapper title={t('page.discover.experts')} withoutToggle>
            <ExpertNavigationBlock attribute={FILTERS.Expert} />
          </FilterWrapper>
        )}
        {!onInsightsPage && (
          <FilterWrapper title={t('page.discover.categories')} withoutToggle={onCategoriesPage}>
            {onCategoriesPage ? (
              <>
                <CategoryNavigationBlock attributes={CATEGORY_FILTER} />
              </>
            ) : (
              <CustomHierarchicalMenu attributes={CATEGORY_FILTER} />
            )}
          </FilterWrapper>
        )}
        {!(onInsightsPage || onGlossaryPage) && (
          <FilterWrapper title={t('page.discover.level')}>
            <CustomRefinementList attribute={FILTERS.Difficulty} />
          </FilterWrapper>
        )}
        {!(onExpertPage || onGlossaryPage || onPathwaysPage) && (
          <FilterWrapper title={t('page.discover.experts')}>
            <CustomRefinementList
              attribute={FILTERS.Expert}
              showMore
              searchable
              limit={5}
              showMoreLimit={50}
              delay={isMobile ? 500 : 0}
            />
          </FilterWrapper>
        )}
        {!(onInsightsPage || onGlossaryPage) && (
          <FilterWrapper title={t('page.discover.duration')}>
            <CustomRefinementList attribute={FILTERS.Duration} />
          </FilterWrapper>
        )}
        {resetTypeFilter && (
          <FilterWrapper title={t('page.discover.type')}>
            <CustomRefinementList attribute={FILTERS.SearchType} />
          </FilterWrapper>
        )}
      </Box>
    </>
  );
};

export default FiltersPanel;
